{
    "$schema": "http://json-schema.org/draft-07/schema#",
    "$id": "https://vrlearning.eu/schemas/lesson-v1.json",
    "title": "Lesson",
    "description": "A VR or AR lesson. The headset app renders any document that validates against this schema; adding a lesson must never require changing the app.",
    "type": "object",
    "additionalProperties": false,
    "required": [
        "schemaVersion",
        "id",
        "title",
        "language",
        "mode",
        "audience",
        "learningGoals",
        "scene",
        "steps"
    ],
    "properties": {
        "schemaVersion": {
            "description": "Bumped only for a breaking change. Additions within v1 must stay backward compatible.",
            "const": 1
        },
        "id": {
            "description": "Stable slug, unique per language variant (solar-system-nl, solar-system-en). Maps to lesson.slug in the database; the database id is a uuid.",
            "$ref": "#/definitions/slug"
        },
        "title": {
            "type": "string",
            "minLength": 1,
            "maxLength": 120
        },
        "language": {
            "description": "Dutch first (ADR-008); each language is its own document, and English is written when a customer needs it (ADR-013).",
            "enum": ["nl", "en"]
        },
        "mode": {
            "description": "Which WebXR session the lesson needs. Must agree with scene.type.",
            "enum": ["vr", "ar"]
        },
        "selfPractice": {
            "description": "Whether a student may start this alone, in the free time a teacher gives them after a lesson. Absent means no: a lesson is teacher-led unless somebody has decided otherwise, which is the safe way round for a list a child browses unsupervised.",
            "type": "boolean",
            "default": false
        },
        "audience": {
            "type": "object",
            "additionalProperties": false,
            "required": ["ageMin"],
            "properties": {
                "ageMin": {
                    "type": "integer",
                    "minimum": 4,
                    "maximum": 99
                },
                "level": {
                    "type": "string",
                    "minLength": 1,
                    "maxLength": 60
                }
            }
        },
        "learningGoals": {
            "description": "Phrased as actions. At most two, per docs/10-pedagogy.md.",
            "type": "array",
            "minItems": 1,
            "maxItems": 2,
            "items": {
                "type": "string",
                "minLength": 1,
                "maxLength": 200
            }
        },
        "scene": {
            "$ref": "#/definitions/scene"
        },
        "objects": {
            "description": "Things the lesson can look at, focus or hang hotspots on. AR lessons usually have none.",
            "type": "object",
            "propertyNames": {
                "$ref": "#/definitions/slug"
            },
            "additionalProperties": {
                "$ref": "#/definitions/lessonObject"
            }
        },
        "steps": {
            "type": "array",
            "minItems": 1,
            "items": {
                "$ref": "#/definitions/step"
            }
        },
        "labels": {
            "description": "When an object's name floats beside it. \"always\" suits a handful of far-apart objects; \"on-demand\" suits many close together, where every name at once is a wall of text rather than a label -- then a name appears only for the step's focus or its hotspots. Default \"always\".",
            "enum": ["always", "on-demand"]
        }
    },
    "definitions": {
        "slug": {
            "type": "string",
            "pattern": "^[a-z0-9]+(-[a-z0-9]+)*$",
            "maxLength": 64
        },
        "worldFeature": {
            "description": "A feature of the lesson's world, as wereld:<feature>: the catalogue lists each world's.",
            "type": "string",
            "pattern": "^wereld:[a-z0-9]+(-[a-z0-9]+)*$",
            "maxLength": 71
        },
        "mediaId": {
            "description": "Media is referenced by id, never by URL (CLAUDE.md). The API issues signed URLs at load time.",
            "type": "string",
            "pattern": "^m_[A-Za-z0-9_-]{1,64}$"
        },
        "vec3": {
            "description": "Metres in the room, [x, y, z].",
            "type": "array",
            "minItems": 3,
            "maxItems": 3,
            "items": {
                "type": "number"
            }
        },
        "scene": {
            "description": "Where the lesson takes place. `type` says which kind of scene, and picks the rules the rest is checked against.",
            "type": "object",
            "discriminator": { "propertyName": "type" },
            "oneOf": [
                {
                    "type": "object",
                    "additionalProperties": false,
                    "required": ["type", "preset"],
                    "properties": {
                        "type": {
                            "const": "procedural"
                        },
                        "preset": {
                            "description": "A scene builder that lives in the headset app. The vocabulary grows per milestone, not per lesson.",
                            "enum": ["solar-system", "campus"]
                        }
                    }
                },
                {
                    "type": "object",
                    "additionalProperties": false,
                    "required": ["type", "media"],
                    "properties": {
                        "type": {
                            "const": "gltf"
                        },
                        "media": {
                            "$ref": "#/definitions/mediaId"
                        }
                    }
                },
                {
                    "type": "object",
                    "additionalProperties": false,
                    "required": ["type"],
                    "properties": {
                        "type": {
                            "description": "A real place in a 360° photo around a flat stage of 6 m radius (ADR-036). VR. One source: want with search, or brief; media, credit and yaw are filled in by the platform.",
                            "const": "pano360"
                        },
                        "media": {
                            "description": "The photo, equirectangular 2:1. Filled in by the platform, never written by a model.",
                            "$ref": "#/definitions/mediaId"
                        },
                        "want": {
                            "description": "Which place and view the photo should show, in Dutch. The platform finds the photo.",
                            "type": "string",
                            "minLength": 1,
                            "maxLength": 200
                        },
                        "search": {
                            "description": "1-6 English words to search for the photo with.",
                            "type": "string",
                            "minLength": 1,
                            "maxLength": 80
                        },
                        "brief": {
                            "description": "The school's own photo n from the brief, 1-4, instead of want and search.",
                            "type": "integer",
                            "minimum": 1,
                            "maximum": 4
                        },
                        "credit": {
                            "description": "Who made the photo and under which licence. Filled in by the platform.",
                            "type": "string",
                            "minLength": 1,
                            "maxLength": 140
                        },
                        "yaw": {
                            "description": "The photo's azimuth, degrees, that faces the pupil at the start. Filled in by the platform; default 0.",
                            "type": "number",
                            "minimum": -180,
                            "maximum": 180
                        }
                    }
                },
                {
                    "type": "object",
                    "additionalProperties": false,
                    "required": ["type"],
                    "properties": {
                        "type": {
                            "const": "ar-room"
                        }
                    }
                },
                {
                    "type": "object",
                    "additionalProperties": false,
                    "required": ["type"],
                    "properties": {
                        "type": {
                            "description": "A neutral room built in code: a soft floor, light from above and a gradient sky, and no media. For VR lessons made of shapes (ADR-030).",
                            "const": "studio"
                        }
                    }
                },
                {
                    "type": "object",
                    "additionalProperties": false,
                    "required": ["type", "world"],
                    "properties": {
                        "type": {
                            "description": "A real-looking place around a flat stage of 6 m radius (ADR-032). VR.",
                            "const": "world"
                        },
                        "world": {
                            "description": "Which world, by its slug: a code world (volcanic-island, science-lab, forest, meadow, polder, beach, sea-floor, town-square, modern-street, museum-hall, villa) or a published world package. Whether it exists is checked against the world catalogue when a lesson is saved, generated or published; a headset that cannot build it shows the studio. Its stage is 6 m round the origin, floor y = 0, unless the world is not staged (the villa: its rooms are its places, for a furnish task only, ADR-037).",
                            "type": "string",
                            "pattern": "^[a-z][a-z0-9-]{1,39}$"
                        }
                    }
                }
            ]
        },
        "lessonObject": {
            "type": "object",
            "additionalProperties": false,
            "required": ["kind", "label"],
            "properties": {
                "kind": {
                    "description": "namespace:name. The engine registers a builder per namespace, which is what makes new object types pluggable.",
                    "type": "string",
                    "pattern": "^(model:[a-z0-9-]{2,40}|[a-z][a-z0-9-]*:[a-z][a-z0-9-]*)$"
                },
                "label": {
                    "type": "string",
                    "minLength": 1,
                    "maxLength": 60
                },
                "pos": {
                    "$ref": "#/definitions/vec3"
                },
                "radius": {
                    "type": "number",
                    "exclusiveMinimum": 0
                },
                "spin": {
                    "description": "Radians per second about the y axis.",
                    "type": "number"
                },
                "tilt": {
                    "description": "Axial tilt in radians.",
                    "type": "number"
                },
                "ring": {
                    "description": "Inner and outer radius as a multiple of the object's radius.",
                    "type": "array",
                    "minItems": 2,
                    "maxItems": 2,
                    "items": {
                        "type": "number",
                        "exclusiveMinimum": 0
                    }
                },
                "parent": {
                    "description": "Another object this one orbits. The Moon is modelled this way rather than as a flag on Earth, so hotspots can target it.",
                    "$ref": "#/definitions/slug"
                },
                "orbit": {
                    "description": "Orbit radius in metres around parent.",
                    "type": "number",
                    "exclusiveMinimum": 0
                },
                "parts": {
                    "description": "What a shape: object is made of (ADR-030). Only a shape takes parts, and a shape must have them. At most 64 in total, counting the parts inside groups, and at most 30 shape objects per lesson: both checked by the validator, because a schema cannot count across levels.",
                    "type": "array",
                    "minItems": 1,
                    "maxItems": 64,
                    "items": { "$ref": "#/definitions/shapePart" }
                },
                "hidden": {
                    "description": "True: not there until a step shows it. VR only.",
                    "type": "boolean"
                },
                "rot": {
                    "description": "A shape only: degrees about x, y and z, turned about its pos.",
                    "$ref": "#/definitions/shapeRotation"
                },
                "scale": {
                    "description": "A shape only: bigger or smaller alike in every direction.",
                    "type": "number",
                    "minimum": 0.1,
                    "maximum": 10
                },
                "width": {
                    "description": "A poster only: the photo's width in metres. Its height follows the photo.",
                    "type": "number",
                    "minimum": 0.4,
                    "maximum": 3
                },
                "facing": {
                    "description": "A poster only: degrees about the vertical axis; 0 faces +z. Absent: it faces where the pupil stands for the first step about it.",
                    "type": "number",
                    "minimum": -360,
                    "maximum": 360
                },
                "caption": {
                    "description": "A poster only: a short Dutch caption under the photo.",
                    "type": "string",
                    "minLength": 1,
                    "maxLength": 80
                },
                "want": {
                    "description": "A poster only: what the photo should show, in Dutch. The platform finds the photo.",
                    "type": "string",
                    "minLength": 1,
                    "maxLength": 200
                },
                "search": {
                    "description": "A poster only: 1-6 English words to search for the photo with.",
                    "type": "string",
                    "minLength": 1,
                    "maxLength": 80
                },
                "media": {
                    "description": "A poster only: the photo. Filled in by the platform, never written by a model.",
                    "$ref": "#/definitions/mediaId"
                },
                "aspect": {
                    "description": "A poster only: the photo's width divided by its height. Filled in by the platform.",
                    "type": "number",
                    "minimum": 0.4,
                    "maximum": 2.5
                },
                "credit": {
                    "description": "A poster only: who made the photo and under which licence. Filled in by the platform.",
                    "type": "string",
                    "minLength": 1,
                    "maxLength": 140
                },
                "turn": {
                    "description": "A model only: degrees about the vertical axis; 0 faces -z, the way the pupil looks at the start, and positive turns left.",
                    "type": "number",
                    "minimum": -360,
                    "maximum": 360
                },
                "size": {
                    "description": "A model only: metres along its size axis. Absent: its real size.",
                    "type": "number",
                    "minimum": 0.05,
                    "maximum": 80
                },
                "above": {
                    "description": "A model that swims or flies only: metres from the land up to the bottom of its box; default 0. The same for the whole lesson.",
                    "type": "number",
                    "minimum": 0,
                    "maximum": 20
                }
            }
        },
        "view": {
            "description": "Where the visitor stands and what they look at. Three forms, all used by the prototypes.",
            "oneOf": [
                {
                    "type": "object",
                    "additionalProperties": false,
                    "required": ["look"],
                    "properties": {
                        "look": {
                            "oneOf": [
                                {
                                    "$ref": "#/definitions/slug"
                                },
                                {
                                    "$ref": "#/definitions/worldFeature"
                                }
                            ]
                        }
                    }
                },
                {
                    "type": "object",
                    "additionalProperties": false,
                    "required": ["pos", "look"],
                    "properties": {
                        "pos": {
                            "$ref": "#/definitions/vec3"
                        },
                        "look": {
                            "oneOf": [
                                {
                                    "$ref": "#/definitions/slug"
                                },
                                {
                                    "$ref": "#/definitions/worldFeature"
                                },
                                {
                                    "$ref": "#/definitions/vec3"
                                }
                            ]
                        }
                    }
                },
                {
                    "type": "object",
                    "additionalProperties": false,
                    "required": ["station"],
                    "properties": {
                        "station": {
                            "$ref": "#/definitions/slug"
                        }
                    }
                }
            ]
        },
        "panel": {
            "description": "Text on the lectern. In a predict step it is the introduction, shown before the question. Limits come from what stayed readable in the prototypes.",
            "type": "object",
            "additionalProperties": false,
            "required": ["title", "text"],
            "properties": {
                "title": {
                    "type": "string",
                    "minLength": 1,
                    "maxLength": 80
                },
                "text": {
                    "type": "string",
                    "minLength": 1,
                    "maxLength": 260
                }
            }
        },
        "hotspot": {
            "type": "object",
            "additionalProperties": false,
            "required": ["title", "text"],
            "properties": {
                "object": {
                    "description": "The object the hotspot hangs on. Exactly one of object, find and toward.",
                    "$ref": "#/definitions/slug"
                },
                "feature": {
                    "description": "A world lesson, instead of object: the feature of the lesson's world the hotspot hangs on, by its id (\"bloedstroom\"), in any state. Its badge sits at the feature.",
                    "type": "string",
                    "minLength": 1,
                    "maxLength": 64
                },
                "find": {
                    "description": "A pano360 lesson only, instead of object: what to look for in the photo, in Dutch. The platform points it.",
                    "type": "string",
                    "minLength": 1,
                    "maxLength": 60
                },
                "point": {
                    "description": "A pano360 lesson of a place with named points, instead of object: the point's name. The platform fills toward.",
                    "type": "string",
                    "minLength": 1,
                    "maxLength": 40
                },
                "toward": {
                    "description": "Where find points in the photo, [azimuth, elevation] in degrees. Filled in by the platform.",
                    "type": "array",
                    "minItems": 2,
                    "maxItems": 2,
                    "items": [
                        { "type": "number", "minimum": -180, "maximum": 180 },
                        { "type": "number", "minimum": -90, "maximum": 90 }
                    ]
                },
                "title": {
                    "type": "string",
                    "minLength": 1,
                    "maxLength": 80
                },
                "text": {
                    "type": "string",
                    "minLength": 1,
                    "maxLength": 200
                }
            }
        },
        "panoPhoto": {
            "description": "A 360° photo from this step on, in a pano360 lesson: the scene's fields without type.",
            "type": "object",
            "additionalProperties": false,
            "properties": {
                "media": {
                    "description": "The photo, equirectangular 2:1. Filled in by the platform, never written by a model.",
                    "$ref": "#/definitions/mediaId"
                },
                "want": {
                    "description": "Which place and view the photo should show, in Dutch. The platform finds the photo.",
                    "type": "string",
                    "minLength": 1,
                    "maxLength": 200
                },
                "search": {
                    "description": "1-6 English words to search for the photo with.",
                    "type": "string",
                    "minLength": 1,
                    "maxLength": 80
                },
                "brief": {
                    "description": "The school's own photo n from the brief, 1-4, instead of want and search.",
                    "type": "integer",
                    "minimum": 1,
                    "maximum": 4
                },
                "credit": {
                    "description": "Who made the photo and under which licence. Filled in by the platform.",
                    "type": "string",
                    "minLength": 1,
                    "maxLength": 140
                },
                "yaw": {
                    "description": "The photo's azimuth, degrees, that faces the pupil at the start. Filled in by the platform; default 0.",
                    "type": "number",
                    "minimum": -180,
                    "maximum": 180
                }
            }
        },
        "quiz": {
            "description": "Retrieval at the end of a lesson. answer is a zero-based index into options.",
            "type": "object",
            "additionalProperties": false,
            "required": ["question", "options", "answer", "explain"],
            "properties": {
                "question": {
                    "type": "string",
                    "minLength": 1,
                    "maxLength": 200
                },
                "options": {
                    "type": "array",
                    "minItems": 2,
                    "maxItems": 4,
                    "items": {
                        "type": "string",
                        "minLength": 1,
                        "maxLength": 80
                    }
                },
                "answer": {
                    "type": "integer",
                    "minimum": 0
                },
                "explain": {
                    "type": "string",
                    "minLength": 1,
                    "maxLength": 260
                },
                "level": {
                    "description": "How hard the question is: herkennen (recognise it), begrijpen (understand why), toepassen (apply it to a new case). A lesson's questions rise: never an easier one after a harder one.",
                    "enum": ["herkennen", "begrijpen", "toepassen"]
                },
                "doordenk": {
                    "description": "The lesson's doordenkvraag: its last question, a reasoning question (why, or what would happen if, in a new case) answered from memory, at level toepassen. The teacher's console shows it after the lesson as a starter for a class discussion.",
                    "type": "boolean"
                }
            }
        },
        "predict": {
            "description": "Asked after the step's panel (its introduction), before the reveal: the task, or the next step. Omit answer when a task decides what was right, as in AR measuring.",
            "type": "object",
            "additionalProperties": false,
            "required": ["question", "options"],
            "properties": {
                "question": {
                    "type": "string",
                    "minLength": 1,
                    "maxLength": 200
                },
                "options": {
                    "type": "array",
                    "minItems": 2,
                    "maxItems": 4,
                    "items": {
                        "type": "string",
                        "minLength": 1,
                        "maxLength": 80
                    }
                },
                "answer": {
                    "type": "integer",
                    "minimum": 0
                }
            }
        },
        "task": {
            "description": "Something the pupil does. measure and assemble: AR, in the real world; furnish: VR, in the villa.",
            "oneOf": [
                {
                    "type": "object",
                    "additionalProperties": false,
                    "required": ["type", "instruction"],
                    "properties": {
                        "type": {
                            "const": "measure"
                        },
                        "instruction": {
                            "type": "string",
                            "minLength": 1,
                            "maxLength": 200
                        },
                        "edges": {
                            "description": "Ascending bucket boundaries in metres. With a predict, there must be one fewer edge than there are options.",
                            "type": "array",
                            "minItems": 1,
                            "items": {
                                "type": "number",
                                "exclusiveMinimum": 0
                            }
                        }
                    }
                },
                {
                    "type": "object",
                    "additionalProperties": false,
                    "required": ["type", "instruction", "parts"],
                    "properties": {
                        "type": {
                            "const": "assemble"
                        },
                        "instruction": {
                            "type": "string",
                            "minLength": 1,
                            "maxLength": 200
                        },
                        "parts": {
                            "description": "Object ids the student has to put in place. Each one's `pos` in `objects` is where it belongs -- the lesson states the finished thing and the engine scatters it, so there is no second copy of the right answer to keep in step.",
                            "type": "array",
                            "minItems": 2,
                            "maxItems": 24,
                            "items": {
                                "$ref": "#/definitions/slug"
                            }
                        },
                        "snapMetres": {
                            "description": "How close counts as placed. Default 0.08. A ten-year-old pinching a bone in mid-air is not accurate to a centimetre, and a lesson about the skeleton is not a test of fine motor control.",
                            "type": "number",
                            "exclusiveMinimum": 0,
                            "maximum": 0.5
                        }
                    }
                },
                {
                    "type": "object",
                    "additionalProperties": false,
                    "required": ["type", "instruction"],
                    "properties": {
                        "type": {
                            "description": "The pupils place the school's own work (furniture, sculptures, cars: a set of werkstukken the teacher picks when the lesson starts, never named in the lesson). Only in a world with a placement space (the villa, or a package with a plaatsruimte), one step per lesson (ADR-037, ADR-050).",
                            "const": "furnish"
                        },
                        "instruction": {
                            "type": "string",
                            "minLength": 1,
                            "maxLength": 200
                        }
                    }
                }
            ]
        },
        "step": {
            "type": "object",
            "additionalProperties": false,
            "required": ["id", "title"],
            "properties": {
                "id": {
                    "$ref": "#/definitions/slug"
                },
                "title": {
                    "type": "string",
                    "minLength": 1,
                    "maxLength": 80
                },
                "focus": {
                    "description": "Which object the step is about; the engine dims the other labels.",
                    "oneOf": [
                        {
                            "$ref": "#/definitions/slug"
                        },
                        {
                            "$ref": "#/definitions/worldFeature"
                        }
                    ]
                },
                "view": {
                    "$ref": "#/definitions/view"
                },
                "panel": {
                    "$ref": "#/definitions/panel"
                },
                "narration": {
                    "description": "What the voice says for this step: spoken Dutch, 15–45 s. Absent: the panel's text, then the step's question. Write numbers and abbreviations out.",
                    "type": "string",
                    "minLength": 1,
                    "maxLength": 600,
                    "pattern": "^ *[^ ].*$"
                },
                "voiceover": {
                    "$ref": "#/definitions/mediaId"
                },
                "hotspots": {
                    "type": "array",
                    "maxItems": 6,
                    "items": {
                        "$ref": "#/definitions/hotspot"
                    }
                },
                "quiz": {
                    "$ref": "#/definitions/quiz"
                },
                "predict": {
                    "$ref": "#/definitions/predict"
                },
                "task": {
                    "$ref": "#/definitions/task"
                },
                "show": {
                    "description": "Objects that appear when this step starts, growing into place. VR only.",
                    "type": "array",
                    "maxItems": 12,
                    "items": { "$ref": "#/definitions/slug" }
                },
                "hide": {
                    "description": "Objects that disappear when this step starts, shrinking away. VR only.",
                    "type": "array",
                    "maxItems": 12,
                    "items": { "$ref": "#/definitions/slug" }
                },
                "move": {
                    "description": "Shapes that move, turn or grow to a new place when this step starts, after show and hide. VR only.",
                    "type": "array",
                    "maxItems": 8,
                    "items": { "$ref": "#/definitions/stepMove" }
                },
                "highlight": {
                    "description": "The object this step points out: it lights up twice once the changes are done. VR only.",
                    "$ref": "#/definitions/slug"
                },
                "world": {
                    "description": "States of the world's features from this step on, by feature: {\"vulkaan\": \"uitbarsting\"}.",
                    "type": "object",
                    "additionalProperties": {
                        "type": "string"
                    }
                },
                "pano": {
                    "description": "Changes the 360° photo from this step on; a pano360 lesson only.",
                    "$ref": "#/definitions/panoPhoto"
                },
                "models": {
                    "description": "What the lesson's models do when this step starts, one entry per model, in this order: walk, face, play. A model with no entry carries on. VR only.",
                    "type": "array",
                    "maxItems": 5,
                    "items": {
                        "$ref": "#/definitions/stepModel"
                    }
                },
                "spannend": {
                    "description": "An exciting version of this step's models, played instead of models only when the teacher turned Spannend on.",
                    "$ref": "#/definitions/stepSpannend"
                },
                "pause": {
                    "description": "A good moment for the teacher to pause the class after this step: only the teacher's console shows it; the lesson never stops by itself.",
                    "type": "boolean"
                }
            },
            "not": {
                "description": "A step asks one thing. predict may accompany a task (estimate, then measure), but quiz and task are alternatives.",
                "required": ["quiz", "task"]
            }
        },
        "stepModel": {
            "description": "What one model does when the step starts: walkTo, then face, then play.",
            "type": "object",
            "additionalProperties": false,
            "required": ["object"],
            "properties": {
                "object": {
                    "$ref": "#/definitions/slug"
                },
                "walkTo": {
                    "description": "Where it walks to, [x, z] on the ground; \"pupil\" only in a spannend part.",
                    "oneOf": [
                        {
                            "type": "array",
                            "minItems": 2,
                            "maxItems": 2,
                            "items": {
                                "type": "number"
                            }
                        },
                        {
                            "const": "pupil"
                        }
                    ]
                },
                "gait": {
                    "description": "How it walks; default lopen.",
                    "enum": ["lopen", "rennen", "zwemmen", "vliegen", "rijden"]
                },
                "face": {
                    "description": "Turns to look at the pupil.",
                    "const": "pupil"
                },
                "turn": {
                    "description": "Turns where it stands to this heading, degrees as an object's turn (0 faces -z, positive turns left), eased, before it plays: the pupil sees another side. Not with walkTo, face or circle; never in a spannend part.",
                    "type": "number",
                    "minimum": -360,
                    "maximum": 360
                },
                "play": {
                    "description": "The animation it plays, one the model has.",
                    "enum": [
                        "staan",
                        "lopen",
                        "rennen",
                        "brullen",
                        "bijten",
                        "eten",
                        "kijken",
                        "liggen",
                        "zwemmen",
                        "vliegen",
                        "springen",
                        "aanvallen",
                        "slapen",
                        "rijden",
                        "werken"
                    ]
                },
                "loop": {
                    "description": "Whether play repeats; default as the library says.",
                    "type": "boolean"
                },
                "then": {
                    "description": "What it does once the rest of this entry is done: after a walk with no play, or after a once-only play; default staan.",
                    "enum": [
                        "staan",
                        "lopen",
                        "rennen",
                        "brullen",
                        "bijten",
                        "eten",
                        "kijken",
                        "liggen",
                        "zwemmen",
                        "vliegen",
                        "springen",
                        "aanvallen",
                        "slapen",
                        "rijden",
                        "werken"
                    ]
                },
                "circle": {
                    "description": "Goes round on a circle while it plays a travelling loop (play), always facing along it. The radius is where the step finds the model; the speed is play's. Not with walkTo, face, gait or loop; never in a spannend part. VR.",
                    "type": "object",
                    "additionalProperties": false,
                    "required": ["direction", "rounds"],
                    "properties": {
                        "around": {
                            "description": "The centre: [x, z] on the ground (default [0, 0], the stage centre), or an object that stays put.",
                            "oneOf": [
                                { "type": "array", "minItems": 2, "maxItems": 2, "items": { "type": "number" } },
                                { "$ref": "#/definitions/slug" }
                            ]
                        },
                        "direction": {
                            "description": "links: counter-clockwise seen from above; rechts: clockwise.",
                            "enum": ["links", "rechts"]
                        },
                        "rounds": {
                            "description": "How many rounds, 0.25 to 4 in quarters, or \"loop\": on until a later step names the model.",
                            "oneOf": [
                                { "type": "number", "minimum": 0.25, "maximum": 4, "multipleOf": 0.25 },
                                { "const": "loop" }
                            ]
                        }
                    }
                }
            }
        },
        "stepSpannend": {
            "description": "The models of a step's exciting version.",
            "type": "object",
            "additionalProperties": false,
            "required": ["models"],
            "properties": {
                "models": {
                    "type": "array",
                    "minItems": 1,
                    "maxItems": 5,
                    "items": {
                        "$ref": "#/definitions/stepModel"
                    }
                }
            }
        },
        "stepMove": {
            "description": "Where a shape ends up; what is left out stays as it was.",
            "type": "object",
            "additionalProperties": false,
            "required": ["object"],
            "properties": {
                "object": { "$ref": "#/definitions/slug" },
                "pos": { "$ref": "#/definitions/vec3" },
                "rot": { "$ref": "#/definitions/shapeRotation" },
                "scale": {
                    "type": "number",
                    "minimum": 0.1,
                    "maximum": 10
                },
                "seconds": {
                    "description": "How long the move takes; default 2.",
                    "type": "number",
                    "minimum": 0.5,
                    "maximum": 10
                }
            }
        },
        "shapeColour": {
            "description": "A flat colour, #rrggbb. Shapes have no textures.",
            "type": "string",
            "pattern": "^#[0-9a-fA-F]{6}$"
        },
        "shapeSize": {
            "description": "Metres, between 5 mm and 10 m: nothing too small to see, nothing bigger than a room.",
            "type": "number",
            "minimum": 0.005,
            "maximum": 10
        },
        "shapeOffset": {
            "description": "Metres from the middle of the object, or of the group the part is in, [x, y, z].",
            "type": "array",
            "minItems": 3,
            "maxItems": 3,
            "items": { "type": "number", "minimum": -10, "maximum": 10 }
        },
        "shapeRotation": {
            "description": "Degrees about the x, y and z axes, [x, y, z].",
            "type": "array",
            "minItems": 3,
            "maxItems": 3,
            "items": { "type": "number", "minimum": -360, "maximum": 360 }
        },
        "shapeOpacity": {
            "description": "1 is solid. Lower lets what is inside show through; below 0.1 a part is as good as invisible.",
            "type": "number",
            "minimum": 0.1,
            "maximum": 1
        },
        "shapeStand": {
            "description": "True on the part a thing stands on -- a plinth, a pedestal -- and not on what a step is about. The engine frames the subject without it, so a plinth on the floor does not drag the view down to the floor. Drawn as any other part.",
            "type": "boolean"
        },
        "shapeFinish": {
            "description": "How the surface looks: matte (the default), gloss (wet or polished) or glow (hot, or giving light).",
            "enum": ["matte", "gloss", "glow"]
        },
        "shapeScale": {
            "description": "Makes everything in the group bigger or smaller alike.",
            "type": "number",
            "minimum": 0.1,
            "maximum": 10
        },
        "profilePoint": {
            "description": "A point of a flat outline in metres, [x, y]. For a lathe, x is the distance from the axis.",
            "type": "array",
            "minItems": 2,
            "maxItems": 2,
            "items": { "type": "number", "minimum": -10, "maximum": 10 }
        },
        "shapeBox": {
            "type": "object",
            "additionalProperties": false,
            "required": ["type", "size", "color"],
            "properties": {
                "type": { "const": "box" },
                "size": {
                    "description": "[width, height, depth]",
                    "type": "array",
                    "minItems": 3,
                    "maxItems": 3,
                    "items": { "$ref": "#/definitions/shapeSize" }
                },
                "pos": { "$ref": "#/definitions/shapeOffset" },
                "rot": { "$ref": "#/definitions/shapeRotation" },
                "color": { "$ref": "#/definitions/shapeColour" },
                "opacity": { "$ref": "#/definitions/shapeOpacity" },
                "stand": { "$ref": "#/definitions/shapeStand" },
                "finish": { "$ref": "#/definitions/shapeFinish" }
            }
        },
        "shapeSphere": {
            "type": "object",
            "additionalProperties": false,
            "required": ["type", "size", "color"],
            "properties": {
                "type": { "const": "sphere" },
                "size": {
                    "description": "[radius]",
                    "type": "array",
                    "minItems": 1,
                    "maxItems": 1,
                    "items": { "$ref": "#/definitions/shapeSize" }
                },
                "pos": { "$ref": "#/definitions/shapeOffset" },
                "rot": { "$ref": "#/definitions/shapeRotation" },
                "color": { "$ref": "#/definitions/shapeColour" },
                "opacity": { "$ref": "#/definitions/shapeOpacity" },
                "stand": { "$ref": "#/definitions/shapeStand" },
                "finish": { "$ref": "#/definitions/shapeFinish" }
            }
        },
        "shapeCylinder": {
            "type": "object",
            "additionalProperties": false,
            "required": ["type", "size", "color"],
            "properties": {
                "type": { "const": "cylinder" },
                "size": {
                    "description": "[top radius, bottom radius, height], standing on the y axis",
                    "type": "array",
                    "minItems": 3,
                    "maxItems": 3,
                    "items": { "$ref": "#/definitions/shapeSize" }
                },
                "pos": { "$ref": "#/definitions/shapeOffset" },
                "rot": { "$ref": "#/definitions/shapeRotation" },
                "color": { "$ref": "#/definitions/shapeColour" },
                "opacity": { "$ref": "#/definitions/shapeOpacity" },
                "stand": { "$ref": "#/definitions/shapeStand" },
                "finish": { "$ref": "#/definitions/shapeFinish" }
            }
        },
        "shapeCone": {
            "type": "object",
            "additionalProperties": false,
            "required": ["type", "size", "color"],
            "properties": {
                "type": { "const": "cone" },
                "size": {
                    "description": "[radius, height], point up",
                    "type": "array",
                    "minItems": 2,
                    "maxItems": 2,
                    "items": { "$ref": "#/definitions/shapeSize" }
                },
                "pos": { "$ref": "#/definitions/shapeOffset" },
                "rot": { "$ref": "#/definitions/shapeRotation" },
                "color": { "$ref": "#/definitions/shapeColour" },
                "opacity": { "$ref": "#/definitions/shapeOpacity" },
                "stand": { "$ref": "#/definitions/shapeStand" },
                "finish": { "$ref": "#/definitions/shapeFinish" }
            }
        },
        "shapeTorus": {
            "type": "object",
            "additionalProperties": false,
            "required": ["type", "size", "color"],
            "properties": {
                "type": { "const": "torus" },
                "size": {
                    "description": "[radius of the ring, radius of the tube], lying in the x-y plane",
                    "type": "array",
                    "minItems": 2,
                    "maxItems": 2,
                    "items": { "$ref": "#/definitions/shapeSize" }
                },
                "pos": { "$ref": "#/definitions/shapeOffset" },
                "rot": { "$ref": "#/definitions/shapeRotation" },
                "color": { "$ref": "#/definitions/shapeColour" },
                "opacity": { "$ref": "#/definitions/shapeOpacity" },
                "stand": { "$ref": "#/definitions/shapeStand" },
                "finish": { "$ref": "#/definitions/shapeFinish" }
            }
        },
        "shapeCapsule": {
            "type": "object",
            "additionalProperties": false,
            "required": ["type", "size", "color"],
            "properties": {
                "type": { "const": "capsule" },
                "size": {
                    "description": "[radius, length of the straight middle], standing on the y axis",
                    "type": "array",
                    "minItems": 2,
                    "maxItems": 2,
                    "items": { "$ref": "#/definitions/shapeSize" }
                },
                "pos": { "$ref": "#/definitions/shapeOffset" },
                "rot": { "$ref": "#/definitions/shapeRotation" },
                "color": { "$ref": "#/definitions/shapeColour" },
                "opacity": { "$ref": "#/definitions/shapeOpacity" },
                "stand": { "$ref": "#/definitions/shapeStand" },
                "finish": { "$ref": "#/definitions/shapeFinish" }
            }
        },
        "shapePlane": {
            "type": "object",
            "additionalProperties": false,
            "required": ["type", "size", "color"],
            "properties": {
                "type": { "const": "plane" },
                "size": {
                    "description": "[width, height], facing +z; seen from both sides",
                    "type": "array",
                    "minItems": 2,
                    "maxItems": 2,
                    "items": { "$ref": "#/definitions/shapeSize" }
                },
                "pos": { "$ref": "#/definitions/shapeOffset" },
                "rot": { "$ref": "#/definitions/shapeRotation" },
                "color": { "$ref": "#/definitions/shapeColour" },
                "opacity": { "$ref": "#/definitions/shapeOpacity" },
                "stand": { "$ref": "#/definitions/shapeStand" },
                "finish": { "$ref": "#/definitions/shapeFinish" }
            }
        },
        "shapeLathe": {
            "description": "An outline turned about the y axis, as on a potter's wheel: a vase, a volcano, a flask.",
            "type": "object",
            "additionalProperties": false,
            "required": ["type", "points", "color"],
            "properties": {
                "type": { "const": "lathe" },
                "points": {
                    "description": "[distance from the axis, height] from bottom to top. A negative distance is drawn as 0.",
                    "type": "array",
                    "minItems": 2,
                    "maxItems": 64,
                    "items": { "$ref": "#/definitions/profilePoint" }
                },
                "pos": { "$ref": "#/definitions/shapeOffset" },
                "rot": { "$ref": "#/definitions/shapeRotation" },
                "color": { "$ref": "#/definitions/shapeColour" },
                "opacity": { "$ref": "#/definitions/shapeOpacity" },
                "stand": { "$ref": "#/definitions/shapeStand" },
                "finish": { "$ref": "#/definitions/shapeFinish" }
            }
        },
        "shapeExtrude": {
            "description": "A flat outline pushed out along z, as a biscuit cutter through dough, centred on its pos: a cross-section, an arrow, a leaf.",
            "type": "object",
            "additionalProperties": false,
            "required": ["type", "outline", "depth", "color"],
            "properties": {
                "type": { "const": "extrude" },
                "outline": {
                    "description": "[x, y] corners in order, in metres. The outline closes itself and must not cross itself.",
                    "type": "array",
                    "minItems": 3,
                    "maxItems": 64,
                    "items": { "$ref": "#/definitions/profilePoint" }
                },
                "depth": { "$ref": "#/definitions/shapeSize" },
                "pos": { "$ref": "#/definitions/shapeOffset" },
                "rot": { "$ref": "#/definitions/shapeRotation" },
                "color": { "$ref": "#/definitions/shapeColour" },
                "opacity": { "$ref": "#/definitions/shapeOpacity" },
                "stand": { "$ref": "#/definitions/shapeStand" },
                "finish": { "$ref": "#/definitions/shapeFinish" }
            }
        },
        "shapeLeaf": {
            "description": "One part that is drawn: a primitive, a lathe or an extrusion. `type` says which, and picks the rules the rest of the part is checked against.",
            "type": "object",
            "discriminator": { "propertyName": "type" },
            "oneOf": [
                { "$ref": "#/definitions/shapeBox" },
                { "$ref": "#/definitions/shapeSphere" },
                { "$ref": "#/definitions/shapeCylinder" },
                { "$ref": "#/definitions/shapeCone" },
                { "$ref": "#/definitions/shapeTorus" },
                { "$ref": "#/definitions/shapeCapsule" },
                { "$ref": "#/definitions/shapePlane" },
                { "$ref": "#/definitions/shapeLathe" },
                { "$ref": "#/definitions/shapeExtrude" }
            ]
        },
        "shapeGroupInner": {
            "description": "A group inside a group: the third and deepest level, so it holds drawn parts only.",
            "type": "object",
            "additionalProperties": false,
            "required": ["type", "parts"],
            "properties": {
                "type": { "const": "group" },
                "parts": {
                    "type": "array",
                    "minItems": 1,
                    "maxItems": 64,
                    "items": { "$ref": "#/definitions/shapeLeaf" }
                },
                "pos": { "$ref": "#/definitions/shapeOffset" },
                "rot": { "$ref": "#/definitions/shapeRotation" },
                "scale": { "$ref": "#/definitions/shapeScale" }
            }
        },
        "shapeGroup": {
            "description": "Parts moved, turned and scaled together. May hold one more level of groups.",
            "type": "object",
            "additionalProperties": false,
            "required": ["type", "parts"],
            "properties": {
                "type": { "const": "group" },
                "parts": {
                    "type": "array",
                    "minItems": 1,
                    "maxItems": 64,
                    "items": {
                        "type": "object",
                        "discriminator": { "propertyName": "type" },
                        "oneOf": [
                            { "$ref": "#/definitions/shapeBox" },
                            { "$ref": "#/definitions/shapeSphere" },
                            { "$ref": "#/definitions/shapeCylinder" },
                            { "$ref": "#/definitions/shapeCone" },
                            { "$ref": "#/definitions/shapeTorus" },
                            { "$ref": "#/definitions/shapeCapsule" },
                            { "$ref": "#/definitions/shapePlane" },
                            { "$ref": "#/definitions/shapeLathe" },
                            { "$ref": "#/definitions/shapeExtrude" },
                            { "$ref": "#/definitions/shapeGroupInner" }
                        ]
                    }
                },
                "pos": { "$ref": "#/definitions/shapeOffset" },
                "rot": { "$ref": "#/definitions/shapeRotation" },
                "scale": { "$ref": "#/definitions/shapeScale" }
            }
        },
        "shapePart": {
            "description": "A part of a shape object: something drawn, or a group of parts. Written out level by level rather than as one recursive definition, so nesting stops at three levels by construction and the structured-output variant can inline it (ADR-030).",
            "type": "object",
            "discriminator": { "propertyName": "type" },
            "oneOf": [
                { "$ref": "#/definitions/shapeBox" },
                { "$ref": "#/definitions/shapeSphere" },
                { "$ref": "#/definitions/shapeCylinder" },
                { "$ref": "#/definitions/shapeCone" },
                { "$ref": "#/definitions/shapeTorus" },
                { "$ref": "#/definitions/shapeCapsule" },
                { "$ref": "#/definitions/shapePlane" },
                { "$ref": "#/definitions/shapeLathe" },
                { "$ref": "#/definitions/shapeExtrude" },
                { "$ref": "#/definitions/shapeGroup" }
            ]
        }
    }
}
